import type { FieldJob } from "@riea-water/contracts";
import { useRef, useState } from "react";
import { useOutletContext } from "react-router";
import { exportFieldJobs, useFieldJobs, useRecordExchange } from "../api/queries.js";
import type { ShellContext } from "../shell/Shell.js";
import { useToast } from "../shell/toast.js";
import { CompleteJobSheet } from "./CompleteJobSheet.js";
import {
  dueText,
  isOpenJob,
  JOB_TYPE_LABEL,
  jobPlace,
  jobStatus,
  needsExchangeRecorded,
} from "./field-helpers.js";

const CAN_COMPLETE = ["tenant_admin", "data_lead", "field_coordinator"];
const CAN_EXPORT = ["tenant_admin", "data_lead", "analyst", "field_coordinator"];

/**
 * Data Assurance · Field jobs (FLD-01 to FLD-04, AC-3-02) as the prototype
 * draws it: job, type, SPID or address, assigned to, raised from, due,
 * status, and Complete on an open job. Export to Field App and Export CSV send
 * the open jobs out and dispatch them (D-58, D-63). A completed exchange the
 * registry never heard about offers Record the exchange (D-61).
 */
export function FieldJobs() {
  const { client, me, getToken } = useOutletContext<ShellContext>();
  const jobs = useFieldJobs(client, { limit: 200 });
  const recordExchange = useRecordExchange(client);
  const { toast, Toast } = useToast();
  const dialog = useRef<HTMLDialogElement>(null);
  const [completing, setCompleting] = useState<FieldJob | null>(null);
  const [exporting, setExporting] = useState(false);

  const items = jobs.data?.items ?? [];
  const open = items.filter(isOpenJob);

  const exportOpen = async (format: "csv" | "job_object") => {
    if (open.length === 0) {
      toast("No open jobs to export");
      return;
    }
    setExporting(true);
    try {
      const out = await exportFieldJobs(
        getToken,
        open.map((j) => j.id),
        format,
      );
      toast(
        `${out.dispatched} job${out.dispatched === 1 ? "" : "s"} exported to ${format === "csv" ? "CSV" : "the Field App"} · FieldJobDispatched recorded${me.hasSyntheticData ? " · synthetic watermark applied" : ""}`,
      );
      await jobs.refetch();
    } catch (e) {
      toast(`Export refused · ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExporting(false);
    }
  };

  const openComplete = (job: FieldJob) => {
    setCompleting(job);
    dialog.current?.showModal();
  };
  const closeComplete = () => {
    dialog.current?.close();
    setCompleting(null);
  };

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Data Assurance · Field</div>
          <h1>Field jobs</h1>
        </div>
        {CAN_EXPORT.includes(me.role) ? (
          <div className="split">
            <button
              className="btn sm"
              type="button"
              disabled={exporting}
              onClick={() => void exportOpen("job_object")}
            >
              Export to Field App
            </button>
            <button
              className="btn sm"
              type="button"
              disabled={exporting}
              onClick={() => void exportOpen("csv")}
            >
              Export CSV
            </button>
          </div>
        ) : null}
      </div>
      <section className="card">
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Job</th>
                <th>Type</th>
                <th>SPID or address</th>
                <th>Assigned to</th>
                <th>Raised from</th>
                <th>Due</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.isPending ? (
                [0, 1, 2].map((i) => (
                  <tr key={i}>
                    <td colSpan={8}>
                      <div className="skeleton" />
                    </td>
                  </tr>
                ))
              ) : jobs.isError ? (
                <tr>
                  <td colSpan={8} className="empty">
                    The field jobs could not be loaded: {jobs.error.message}. Try again in a moment.
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty">
                    No field jobs yet. Raise one from a finding or a supply point.
                  </td>
                </tr>
              ) : (
                items.map((job) => {
                  const status = jobStatus(job);
                  return (
                    <tr key={job.id}>
                      <td className="mono">{job.ref}</td>
                      <td>{JOB_TYPE_LABEL[job.type]}</td>
                      <td className="mono" style={{ fontSize: ".78rem" }}>
                        {jobPlace(job)}
                      </td>
                      <td>{job.assigneePartyName ?? ""}</td>
                      <td className="mono">{job.findingRef ?? ""}</td>
                      <td className="mono" style={{ fontSize: ".78rem" }}>
                        {dueText(job)}
                      </td>
                      <td>
                        <span className={`pill ${status.tone}`}>{status.label}</span>
                      </td>
                      <td>
                        {isOpenJob(job) && CAN_COMPLETE.includes(me.role) ? (
                          <button
                            className="btn sm"
                            type="button"
                            onClick={() => openComplete(job)}
                            aria-label={`Complete ${job.ref}`}
                          >
                            Complete
                          </button>
                        ) : needsExchangeRecorded(job) && CAN_COMPLETE.includes(me.role) ? (
                          <button
                            className="btn sm primary"
                            type="button"
                            disabled={recordExchange.isPending}
                            onClick={() =>
                              recordExchange.mutate(job.id, {
                                onSuccess: (done) =>
                                  toast(
                                    `${done.ref} · MeterExchanged recorded with the job as evidence · linked findings re-checked`,
                                  ),
                                onError: (e) => toast(`Not recorded · ${e.message}`),
                              })
                            }
                          >
                            Record the exchange
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      <p className="faint" style={{ fontSize: ".78rem", margin: 0 }}>
        Completing a job emits registry events with the job as evidence and re-checks linked
        findings.
      </p>
      <dialog className="sheet" ref={dialog} aria-labelledby="complete-job-title">
        <CompleteJobSheet
          client={client}
          getToken={getToken}
          job={completing}
          onClose={closeComplete}
          onDone={(message) => {
            closeComplete();
            toast(message);
          }}
        />
      </dialog>
      <Toast />
    </>
  );
}
